import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Sparkles, X, CheckCircle2, AlertTriangle, XCircle, Download, Image,
  Maximize2, Minimize2, PanelRight, FileText
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FullscreenToolResult, FullscreenAccessGuard, FullscreenAuditTrail } from './WorkspaceModals';

function ExportBar({ containerRef, title }) {
  const [exporting, setExporting] = useState(false);
  const exportPng = useCallback(async () => {
    if (!containerRef.current) return;
    setExporting(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(containerRef.current, { backgroundColor: '#ffffff', quality: 0.95 });
      const link = document.createElement('a');
      link.download = `${(title || 'export').replace(/\s+/g, '_')}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) { console.error('PNG export failed:', e); }
    finally { setExporting(false); }
  }, [containerRef, title]);

  const exportSvg = useCallback(async () => {
    if (!containerRef.current) return;
    setExporting(true);
    try {
      const { toSvg } = await import('html-to-image');
      const dataUrl = await toSvg(containerRef.current, { backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `${(title || 'export').replace(/\s+/g, '_')}.svg`;
      link.href = dataUrl;
      link.click();
    } catch (e) { console.error('SVG export failed:', e); }
    finally { setExporting(false); }
  }, [containerRef, title]);

  return (
    <div className="flex items-center gap-2">
      <button onClick={exportPng} disabled={exporting} className="h-7 px-2.5 text-[10px] font-medium border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1 transition-colors disabled:opacity-50" data-testid="export-png">
        <Image className="w-3 h-3" /> PNG
      </button>
      <button onClick={exportSvg} disabled={exporting} className="h-7 px-2.5 text-[10px] font-medium border border-[#E5E7EB] rounded-lg hover:bg-[#F3F4F6] flex items-center gap-1 transition-colors disabled:opacity-50" data-testid="export-svg">
        <Download className="w-3 h-3" /> SVG
      </button>
    </div>
  );
}

export function FullscreenOutputModal({ data, onClose }) {
  const contentRef = useRef(null);
  // Mind Map defaults to dock-right because the user wants to keep the chat visible while
  // exploring nodes (clicking a node sends a contextual synthesis prompt to chat — they
  // need to see both panes simultaneously).  Every other output type starts centred.
  const initialMode = data?.type === 'mindmap' ? 'dock-right' : 'fullscreen';
  // 'fullscreen' (default centered overlay) | 'dock-right' (right-half drawer, chat visible) | 'maximize' (true full viewport).
  const [mode, setMode] = useState(initialMode);
  const getTitle = () => {
    if (data.type === 'summary') return 'Summary';
    if (data.type === 'mindmap') return 'Mind Map';
    if (data.type === 'agent') return `${data.data?.agent_type?.replace('custom:', '')} Agent Result`;
    if (data.type === 'tool_result') return data.data?.tool_id?.replace(/_/g, ' ') || 'Tool Result';
    if (data.type === 'access_guard') return 'Access Guard';
    if (data.type === 'audit_trail') return 'Audit Trail';
    return 'Output';
  };
  const showExport = data.type === 'mindmap' || data.type === 'tool_result';

  // Per-mode container styling. Backdrop only blocks pointer events when modal is centred.
  const isDockRight = mode === 'dock-right';
  const isMaximize = mode === 'maximize';
  const overlayClass = isDockRight
    ? 'fixed inset-y-0 right-0 z-50 flex items-stretch justify-end pointer-events-none'
    : 'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4';
  const containerClass = isDockRight
    ? 'bg-white w-[min(720px,55vw)] h-full shadow-2xl flex flex-col overflow-hidden border-l border-[#E5E7EB] pointer-events-auto'
    : isMaximize
      ? 'bg-white w-screen h-screen flex flex-col overflow-hidden'
      : 'bg-white w-full h-full max-w-[95vw] max-h-[95vh] rounded-xl shadow-2xl flex flex-col overflow-hidden';

  return (
    <div
      className={overlayClass}
      onClick={isDockRight ? undefined : onClose}
      data-testid="fullscreen-modal-overlay"
      data-mode={mode}
    >
      <div
        className={containerClass}
        onClick={e => e.stopPropagation()}
        data-testid="fullscreen-modal"
      >
        <div className="h-12 border-b border-[#E5E7EB] flex items-center justify-between px-5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#6590FF]" />
            <span className="text-sm font-bold text-[#111827] capitalize" style={{ fontFamily: 'var(--font-heading)' }}>{getTitle()}</span>
          </div>
          <div className="flex items-center gap-2">
            {showExport && <ExportBar containerRef={contentRef} title={getTitle()} />}
            <ModeToggle mode={mode} setMode={setMode} />
            <button onClick={onClose} className="p-1.5 hover:bg-[#F3F4F6] rounded-lg transition-colors" data-testid="close-fullscreen" title="Close">
              <X className="w-5 h-5 text-[#4B5563]" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto" ref={contentRef}>
          {data.type === 'summary' && <FullscreenSummary data={data.data} />}
          {data.type === 'mindmap' && <FullscreenMindMap data={data.data} workspaceId={data.workspaceId} />}
          {data.type === 'agent' && <FullscreenAgentResult data={data.data} />}
          {data.type === 'tool_result' && <FullscreenToolResult data={data.data} />}
          {data.type === 'access_guard' && <FullscreenAccessGuard data={data.data} />}
          {data.type === 'audit_trail' && <FullscreenAuditTrail data={data.data} />}
        </div>
      </div>
    </div>
  );
}

function ModeToggle({ mode, setMode }) {
  const btn = (target, Icon, label, testId) => {
    const active = mode === target;
    return (
      <button
        type="button"
        onClick={() => setMode(target)}
        className={`p-1.5 rounded-lg transition-colors ${active ? 'bg-[#EEF2FF] text-[#6590FF]' : 'hover:bg-[#F3F4F6] text-[#4B5563]'}`}
        title={label}
        aria-label={label}
        aria-pressed={active}
        data-testid={testId}
      >
        <Icon className="w-4 h-4" />
      </button>
    );
  };
  return (
    <div className="flex items-center gap-1 border border-[#E5E7EB] rounded-lg p-0.5" role="group" aria-label="Layout mode">
      {btn('dock-right', PanelRight, 'Dock to right', 'mode-dock-right')}
      {btn('fullscreen', Minimize2, 'Centered window', 'mode-centered')}
      {btn('maximize', Maximize2, 'Maximize', 'mode-maximize')}
    </div>
  );
}

function FullscreenSummary({ data }) {
  return (
    <div className="max-w-4xl mx-auto p-8">
      {data.documents_analyzed?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {data.documents_analyzed.map((d, i) => (
            <span key={`doc-${i}`} className="text-xs px-2.5 py-1 bg-[#EEF2FF] text-[#6590FF] rounded-full font-medium">{d}</span>
          ))}
          <span className="text-xs px-2.5 py-1 bg-[#F3F4F6] text-[#4B5563] rounded-full">{data.chunks_processed} chunks analyzed</span>
        </div>
      )}
      <div className="prose prose-base max-w-none text-[#111827] leading-relaxed">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.summary}</ReactMarkdown>
      </div>
    </div>
  );
}

function FullscreenAgentResult({ data }) {
  const result = data.result || {};
  const issues = result.issues || result.conflicts || result.recommendations || result.findings || [];
  return (
    <div className="max-w-5xl mx-auto p-8">
      <AgentHeader data={data} result={result} />
      {result.summary && <div className="mb-6 p-5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg"><p className="text-sm text-[#4B5563] leading-relaxed">{result.summary}</p></div>}
      {issues.length > 0 && <IssuesList issues={issues} agentType={data.agent_type} />}
      <ResultGrids result={result} />
    </div>
  );
}

function AgentHeader({ data, result }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span className="text-sm font-bold capitalize text-[#111827]">{data.agent_type?.replace('custom:', '')}</span>
      {result.risk_level && (
        <span className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${result.risk_level === 'high' || result.risk_level === 'critical' ? 'bg-[#FEE2E2] text-[#DC2626]' : result.risk_level === 'medium' ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#ECFDF5] text-[#059669]'}`}>{result.risk_level} risk</span>
      )}
      {result.conflict_count !== undefined && <span className="px-3 py-1 text-xs font-bold bg-[#FEE2E2] text-[#DC2626] rounded-full">{result.conflict_count} conflicts</span>}
      <span className="text-xs text-[#6B7280]">{new Date(data.started_at).toLocaleString()}</span>
    </div>
  );
}

function IssuesList({ issues, agentType }) {
  const title = agentType === 'compliance' ? 'Issues Found' : agentType === 'conflict' ? 'Conflicts Detected' : 'Recommendations';
  return (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-[#111827] mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{title} ({issues.length})</h3>
      <div className="space-y-3">
        {issues.map((item, i) => <IssueCard key={item.title || item.issue || `issue-${i}`} item={item} index={i} />)}
      </div>
    </div>
  );
}

function IssueCard({ item, index }) {
  const sev = item.severity || item.priority || item.type || 'info';
  const sevClass = (sev === 'high' || sev === 'critical') ? 'bg-[#FEE2E2] text-[#DC2626]' : sev === 'medium' ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#F3F4F6] text-[#4B5563]';
  return (
    <div className="p-4 border border-[#E5E7EB] rounded-lg hover:border-[#D1D5DB] transition-colors">
      <div className="flex items-start gap-3">
        <span className={`flex-shrink-0 mt-0.5 px-2 py-0.5 text-[10px] font-bold uppercase rounded ${sevClass}`}>{sev}</span>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-[#111827] mb-1">{item.title || item.name || `Item ${index + 1}`}</h4>
          <p className="text-xs text-[#4B5563] leading-relaxed mb-2">{item.description || ''}</p>
          {item.recommendation && <div className="p-2.5 bg-[#EEF2FF] rounded-xl"><p className="text-xs text-[#6590FF]"><span className="font-semibold">Recommendation:</span> {item.recommendation}</p></div>}
          {item.resolution_suggestion && <div className="p-2.5 bg-[#ECFDF5] rounded-xl"><p className="text-xs text-[#059669]"><span className="font-semibold">Resolution:</span> {item.resolution_suggestion}</p></div>}
          {item.suggested_text && <div className="p-2.5 bg-[#ECFDF5] rounded-xl mt-1"><p className="text-xs text-[#059669]"><span className="font-semibold">Suggested:</span> {item.suggested_text}</p></div>}
          {item.rationale && <p className="text-[11px] text-[#6B7280] mt-1 italic">{item.rationale}</p>}
          {(item.source_document || item.document_a) && <p className="text-[10px] text-[#9CA3AF] mt-1">Source: {item.source_document || `${item.document_a} vs ${item.document_b}`}</p>}
        </div>
      </div>
    </div>
  );
}

function ResultGrids({ result }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {result.compliant_areas?.length > 0 && <ResultGrid title="Compliant Areas" items={result.compliant_areas} bg="bg-[#ECFDF5]" border="border-[#059669]/20" titleColor="text-[#059669]" icon={<CheckCircle2 className="w-3 h-3 text-[#059669] mt-0.5 flex-shrink-0" />} />}
      {result.strengths?.length > 0 && <ResultGrid title="Strengths" items={result.strengths} bg="bg-[#ECFDF5]" border="border-[#059669]/20" titleColor="text-[#059669]" icon={<CheckCircle2 className="w-3 h-3 text-[#059669] mt-0.5 flex-shrink-0" />} />}
      {result.missing_elements?.length > 0 && <ResultGrid title="Missing Elements" items={result.missing_elements} bg="bg-[#FEF3C7]" border="border-[#D97706]/20" titleColor="text-[#92400E]" icon={<AlertTriangle className="w-3 h-3 text-[#D97706] mt-0.5 flex-shrink-0" />} />}
      {result.gaps?.length > 0 && <ResultGrid title="Gaps" items={result.gaps} bg="bg-[#FEE2E2]" border="border-[#DC2626]/20" titleColor="text-[#DC2626]" icon={<XCircle className="w-3 h-3 text-[#DC2626] mt-0.5 flex-shrink-0" />} />}
      {result.consistent_areas?.length > 0 && (
        <div className="p-4 bg-[#EEF2FF] border border-[#6590FF]/20 rounded-lg">
          <h4 className="text-xs font-bold text-[#6590FF] uppercase tracking-wider mb-2">Consistent Areas</h4>
          <ul className="space-y-1">{result.consistent_areas.map((c, i) => <li key={`consistent-${i}`} className="text-xs text-[#4B5563]">{c}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

function ResultGrid({ title, items, bg, border, titleColor, icon }) {
  return (
    <div className={`p-4 ${bg} border ${border} rounded-lg`}>
      <h4 className={`text-xs font-bold ${titleColor} uppercase tracking-wider mb-2`}>{title}</h4>
      <ul className="space-y-1">{items.map((a, i) => <li key={`list-${i}`} className="text-xs text-[#4B5563] flex items-start gap-1.5">{icon}{a}</li>)}</ul>
    </div>
  );
}

export function FullscreenMindMap({ data, workspaceId }) {
  const [activeLeaf, setActiveLeaf] = useState(null); // { label, breadcrumb, references }
  if (!data?.branches) return <div className="p-8 text-center text-[#6B7280]">No mind map data</div>;
  const BRANCH_COLORS = ['#6590FF', '#DC2626', '#059669', '#D97706', '#7C3AED', '#0891B2', '#BE185D', '#4338CA'];

  return (
    <div className="w-full h-full flex" data-testid="mindmap-tree">
      <div className="flex-1 overflow-auto p-6">
        <div className="inline-flex items-center min-w-max">
          {/* Root node */}
          <MindMapNode
            label={data.central_topic}
            color="#6590FF"
            isRoot
            defaultExpanded
            references={data.references}
            breadcrumb={[]}
            onLeafClick={setActiveLeaf}
            children={data.branches?.map((b, i) => ({ ...b, _color: BRANCH_COLORS[i % BRANCH_COLORS.length] }))}
          />
        </div>
      </div>
      {activeLeaf && (
        <MindMapLeafDrawer
          leaf={activeLeaf}
          workspaceId={workspaceId}
          onClose={() => setActiveLeaf(null)}
        />
      )}
    </div>
  );
}

function MindMapNode({ label, color, isRoot, depth = 0, defaultExpanded = false, children: childBranches, references, breadcrumb = [], onLeafClick }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const children = childBranches || [];
  const hasChildren = children.length > 0;
  const isLeaf = !hasChildren && !isRoot;
  const hasRefs = Array.isArray(references) && references.length > 0;
  const nodeRef = useRef(null);

  const handleClick = () => {
    if (hasChildren) {
      setExpanded(prev => !prev);
    } else if (isLeaf && onLeafClick) {
      // Leaf click → open side drawer with supporting source excerpts.
      onLeafClick({ label, breadcrumb, references: references || null });
    }
  };

  // Cursor + visual affordance: leaves are clickable too (for navigation), with a subtle dot
  // indicator when refs are available. Hover styling ramps up to signal interactivity.
  const isClickable = hasChildren || (isLeaf && !!onLeafClick);

  return (
    <div className="flex items-center" style={{ minHeight: 34 }}>
      {/* This node */}
      <div className="flex flex-col items-center justify-center flex-shrink-0" ref={nodeRef}>
        <button
          onClick={handleClick}
          className={`relative flex items-center gap-2 px-4 py-1.5 transition-all duration-200 select-none ${
            isRoot
              ? 'rounded-2xl text-white shadow-lg hover:shadow-xl'
              : depth === 0
              ? 'rounded-xl border-2 bg-white shadow-sm hover:shadow-md'
              : 'rounded-xl border bg-white hover:bg-[#F8FAFF]'
          } ${isClickable ? 'cursor-pointer' : 'cursor-default'} ${isLeaf && hasRefs ? 'hover:border-[#6590FF]' : ''}`}
          style={{
            backgroundColor: isRoot ? color : 'white',
            borderColor: !isRoot ? `${color}${depth === 0 ? '60' : '30'}` : undefined,
            maxWidth: 280,
          }}
          data-testid={isLeaf ? 'tree-node-leaf' : `tree-node-${(label || '').replace(/\s/g, '-')}`}
          data-leaf={isLeaf || undefined}
          data-leaf-label={isLeaf ? label : undefined}
          aria-label={isLeaf ? `Open source excerpts for ${label}` : label}
        >
          <span
            className="text-sm font-semibold truncate"
            style={{
              color: isRoot ? 'white' : depth === 0 ? color : '#374151',
              fontWeight: isRoot || depth === 0 ? 700 : 500,
              fontFamily: 'var(--font-heading), system-ui, sans-serif',
              fontSize: isRoot ? 15 : depth === 0 ? 13 : 12,
            }}
          >
            {label}
          </span>
          {/* Leaf indicator: small dot when there are source refs available. */}
          {isLeaf && hasRefs && (
            <span
              className="flex-shrink-0 w-1.5 h-1.5 rounded-full"
              style={{ backgroundColor: color, opacity: 0.7 }}
              title={`${references.length} supporting excerpt${references.length === 1 ? '' : 's'}`}
            />
          )}
          {hasChildren && (
            <span
              className="flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0 transition-transform duration-200"
              style={{
                backgroundColor: isRoot ? 'rgba(255,255,255,0.25)' : `${color}15`,
                color: isRoot ? 'white' : color,
                transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              &#9654;
            </span>
          )}
        </button>
      </div>

      {/* Connector line + children */}
      {hasChildren && expanded && (
        <div className="flex items-center">
          {/* Horizontal connector from parent */}
          <svg width="60" height="100%" className="flex-shrink-0 self-center" style={{ minHeight: 34 }}>
            <line x1="0" y1="50%" x2="60" y2="50%" stroke={color} strokeWidth="2" strokeOpacity="0.3" />
            <circle cx="0" cy="50%" r="3" fill={color} fillOpacity="0.4" />
            <circle cx="60" cy="50%" r="3" fill={color} fillOpacity="0.4" />
          </svg>

          {/* Children column with vertical rail + connectors */}
          <ChildrenColumn
            children={children}
            color={color}
            depth={depth}
            breadcrumb={[...breadcrumb, label]}
            onLeafClick={onLeafClick}
          />
        </div>
      )}
    </div>
  );
}

function ChildrenColumn({ children, color, depth, breadcrumb = [], onLeafClick }) {
  const containerRef = useRef(null);
  const connectorRefs = useRef([]);
  const [railStyle, setRailStyle] = useState(null);

  useEffect(() => {
    const updateRail = () => {
      if (children.length < 2 || !containerRef.current) {
        setRailStyle(null);
        return;
      }
      const firstSvg = connectorRefs.current[0];
      const lastSvg = connectorRefs.current[children.length - 1];
      if (!firstSvg || !lastSvg) { setRailStyle(null); return; }
      const containerRect = containerRef.current.getBoundingClientRect();
      const firstRect = firstSvg.getBoundingClientRect();
      const lastRect = lastSvg.getBoundingClientRect();
      // SVGs are self-center aligned, so their vertical midpoint IS the node center
      const top = firstRect.top + firstRect.height / 2 - containerRect.top;
      const bottom = lastRect.top + lastRect.height / 2 - containerRect.top;
      if (bottom > top) {
        setRailStyle({ top, height: bottom - top });
      } else {
        setRailStyle(null);
      }
    };
    // Run immediately + on any layout change
    updateRail();
    const timer = setTimeout(updateRail, 50);
    const observer = new ResizeObserver(updateRail);
    if (containerRef.current) observer.observe(containerRef.current);
    // Also observe each child row for size changes (expand/collapse)
    const rows = containerRef.current?.children;
    if (rows) { for (let i = 0; i < rows.length; i++) { if (rows[i].tagName !== 'DIV' || rows[i].style.position === 'absolute') continue; observer.observe(rows[i]); } }
    return () => { observer.disconnect(); clearTimeout(timer); };
  }, [children.length]);

  return (
    <div className="relative flex flex-col" style={{ gap: 2 }} ref={containerRef}>
      {children.map((child, i) => {
        const childColor = child._color || color;
        return (
          <div key={`child-${i}`} className="flex items-center" style={{ minHeight: 34 }}>
            <svg ref={el => connectorRefs.current[i] = el} width="32" height="34" className="flex-shrink-0 self-center" style={{ minHeight: 34 }}>
              <line x1="0" y1="17" x2="32" y2="17" stroke={childColor} strokeWidth="1.5" strokeOpacity="0.3" />
              <circle cx="32" cy="17" r="2.5" fill={childColor} fillOpacity="0.4" />
            </svg>
            <MindMapNode
              label={child.label}
              color={childColor}
              depth={depth + 1}
              defaultExpanded={false}
              references={child.references}
              breadcrumb={breadcrumb}
              onLeafClick={onLeafClick}
              children={child.children?.map(c => ({ ...c, _color: childColor }))}
            />
          </div>
        );
      })}
      {railStyle && railStyle.height > 0 && (
        <div
          className="absolute left-0 w-0.5 rounded-full pointer-events-none"
          style={{
            backgroundColor: `${color}25`,
            top: railStyle.top,
            height: railStyle.height,
          }}
        />
      )}
    </div>
  );
}

// ===== Leaf Drawer (NotebookLM-style click-to-content) =====================
// Slides in from the right side of the FullscreenMindMap when a leaf is clicked.
// Shows top-3 supporting source excerpts with confidence chips, plus two actions
// per excerpt: "Open source" (auto-scrolls + highlights inside SourceDetailPopup)
// and "Ask about this" (closes the modal and pre-fills chat with a contextual prompt).

export function MindMapLeafDrawer({ leaf, workspaceId, onClose }) {
  const [refs, setRefs] = useState(leaf?.references || null);
  const [loading, setLoading] = useState(!leaf?.references);
  const [error, setError] = useState(null);
  const [openSource, setOpenSource] = useState(null);  // { source, highlightChunkText, highlightPage }

  // Lazy-fetch refs for legacy mind maps (no baked-in references) when a leaf is clicked.
  useEffect(() => {
    let cancelled = false;
    if (leaf?.references && leaf.references.length > 0) {
      setRefs(leaf.references);
      setLoading(false);
      setError(null);
      return undefined;
    }
    if (!workspaceId || !leaf?.label) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);
    setRefs(null);
    (async () => {
      try {
        const { workspaceAPI } = await import('../api');
        const r = await workspaceAPI.getMindmapNodeRefs(workspaceId, {
          label: leaf.label,
          breadcrumb: leaf.breadcrumb || [],
          top_k: 3,
        });
        if (cancelled) return;
        setRefs(r.data?.references || []);
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || e.message || 'Failed to load supporting excerpts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [leaf, workspaceId]);

  if (!leaf) return null;
  return (
    <>
      <div
        className="w-[420px] flex-shrink-0 bg-white border-l border-[#E5E7EB] flex flex-col h-full overflow-hidden animate-in slide-in-from-right duration-200"
        data-testid="mindmap-leaf-drawer"
      >
        <LeafDrawerHeader leaf={leaf} onClose={onClose} workspaceId={workspaceId} />
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFBFC]">
          {loading && (
            <div className="flex items-center justify-center py-12 text-[#6590FF]">
              <Loader2Icon />
              <span className="ml-2 text-xs font-medium">Finding supporting excerpts…</span>
            </div>
          )}
          {error && !loading && (
            <div className="rounded-xl border border-[#FDE68A] bg-[#FEFCE8] p-4">
              <p className="text-xs text-[#92400E] font-medium">Couldn&apos;t fetch supporting content.</p>
              <p className="text-[11px] text-[#92400E]/80 mt-1">{error}</p>
            </div>
          )}
          {!loading && !error && refs && refs.length === 0 && (
            <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-white p-6 text-center">
              <p className="text-sm font-medium text-[#374151]">No strong matches found</p>
              <p className="text-xs text-[#6B7280] mt-1.5 leading-relaxed">
                This node didn&apos;t map to any high-confidence excerpt in your sources. It may represent a synthesised concept rather than a literal section.
              </p>
            </div>
          )}
          {!loading && !error && refs && refs.length > 0 && refs.map((r, i) => (
            <ReferenceCard
              key={`ref-${i}-${r.source_id}`}
              reference={r}
              workspaceId={workspaceId}
              leafLabel={leaf.label}
              leafBreadcrumb={leaf.breadcrumb}
              onOpenSource={(payload) => setOpenSource(payload)}
              onClose={onClose}
            />
          ))}
        </div>
      </div>
      {openSource && (
        <LazySourceDetailPopup
          source={openSource.source}
          workspaceId={workspaceId}
          onClose={() => setOpenSource(null)}
          highlightChunkText={openSource.highlightChunkText}
          highlightPage={openSource.highlightPage}
        />
      )}
    </>
  );
}

// Build the enterprise-grade contextual synthesis prompt for a clicked mind-map node.
// Implements the user's "Enhanced Enterprise Version" template (with the deepest
// breadcrumb segment as the parent context — that's the immediate scope the node
// belongs to). Falls back gracefully when there's no breadcrumb (root-level node).
export function buildNodeSynthesisPrompt(leaf) {
  const selected = (leaf?.label || '').trim();
  if (!selected) return '';
  const trail = (leaf?.breadcrumb || []).filter(Boolean).map(s => s.trim());
  // Drop the leaf's own label if it's at the end of the breadcrumb (some callers include it).
  const parentTrail = trail.filter(s => s.toLowerCase() !== selected.toLowerCase());
  const parentContext = parentTrail.length > 0
    ? parentTrail.join(' › ')
    : 'the broader workspace';

  return (
    `Analyze "${selected}" within the broader context of "${parentContext}". ` +
    `Summarize key findings, relationships, trends, dependencies, risks, opportunities, ` +
    `and strategic implications across the connected sources. ` +
    `Where possible, identify anomalies, cross-reference related entities, and suggest ` +
    `follow-up exploration areas that the user can dive into next.`
  );
}

function LeafDrawerHeader({ leaf, onClose, workspaceId }) {
  const breadcrumb = (leaf.breadcrumb || []).filter(Boolean);

  const handleDiscussInChat = () => {
    // Enterprise-aware synthesis prompt: anchors the selected node inside its parent
    // hierarchy so the LLM does multi-document, context-grounded reasoning rather than
    // a flat "summarize this concept" answer.
    const prompt = buildNodeSynthesisPrompt(leaf);
    if (!prompt) return;
    try {
      sessionStorage.setItem(`bkw:wsAskFollowup:${workspaceId}`, prompt);
      window.dispatchEvent(new CustomEvent('bkw:askFollowup', { detail: { workspaceId, prompt } }));
    } catch (e) { console.error(e); }
    if (onClose) onClose();
  };

  return (
    <div className="border-b border-[#E5E7EB] bg-gradient-to-br from-[#F8FAFF] to-white px-4 py-3 flex-shrink-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {breadcrumb.length > 0 && (
            <div className="text-[10px] text-[#6B7280] font-medium truncate mb-1" data-testid="leaf-breadcrumb">
              {breadcrumb.join(' › ')}
            </div>
          )}
          <h3 className="text-base font-bold text-[#111827] leading-snug" style={{ fontFamily: 'var(--font-heading)' }} data-testid="leaf-title">
            {leaf.label}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1.5 hover:bg-[#F3F4F6] rounded-lg transition-colors"
          aria-label="Close"
          data-testid="leaf-drawer-close"
        >
          <X className="w-4 h-4 text-[#6B7280]" />
        </button>
      </div>
      {/* Primary action: send an enterprise-grade contextual synthesis prompt to chat
          so the user can immediately discuss this node + its parent context with the LLM. */}
      <button
        onClick={handleDiscussInChat}
        className="mt-3 w-full h-9 px-3 rounded-xl bg-[#6590FF] hover:bg-[#4B7CFF] text-white text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm"
        data-testid="leaf-discuss-in-chat"
      >
        <span aria-hidden="true">✨</span> Discuss in chat with full context
      </button>
    </div>
  );
}

function ReferenceCard({ reference, workspaceId, leafLabel, leafBreadcrumb, onOpenSource, onClose }) {
  const confColor = {
    high: 'bg-[#ECFDF5] text-[#059669] border-[#059669]/20',
    medium: 'bg-[#EEF2FF] text-[#6590FF] border-[#6590FF]/20',
    low: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]',
  }[reference.confidence] || 'bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]';

  const handleOpenSource = () => {
    onOpenSource({
      source: {
        id: reference.source_id,
        title: reference.source_title,
        source_type: reference.source_type,
      },
      highlightChunkText: reference.chunk_text,
      highlightPage: reference.page_number,
    });
  };

  const handleAskAbout = () => {
    // Excerpt-grounded follow-up: combines the enterprise contextual frame with the
    // specific excerpt the user clicked, so the LLM can ground its synthesis in the exact
    // chunk while still preserving the broader hierarchy.
    const parentTrail = (leafBreadcrumb || []).filter(s => s && s.trim().toLowerCase() !== (leafLabel || '').trim().toLowerCase());
    const parentContext = parentTrail.length > 0 ? parentTrail.join(' › ') : 'the broader workspace';
    const excerpt = (reference.chunk_text || '').slice(0, 240).trim();
    const prompt = (
      `Analyze "${leafLabel}" within the broader context of "${parentContext}". ` +
      `Anchor your answer in this excerpt from "${reference.source_title}": "${excerpt}". ` +
      `Discuss key findings, relationships, and strategic implications, and suggest follow-up areas to explore.`
    );
    try {
      sessionStorage.setItem(`bkw:wsAskFollowup:${workspaceId}`, prompt);
      window.dispatchEvent(new CustomEvent('bkw:askFollowup', { detail: { workspaceId, prompt } }));
    } catch (e) { console.error(e); }
    if (onClose) onClose();
  };

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] p-3.5 hover:border-[#6590FF]/40 transition-colors" data-testid="leaf-reference-card">
      <div className="flex items-start gap-2 mb-2">
        <FileText className="w-3.5 h-3.5 text-[#6B7280] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-semibold text-[#111827] truncate" data-testid="leaf-ref-title">{reference.source_title}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={`text-[9.5px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${confColor}`}>
              {reference.confidence}
            </span>
            {reference.page_number != null && (
              <span className="text-[10.5px] text-[#6B7280]">Page {reference.page_number}</span>
            )}
            <span className="text-[10.5px] text-[#9CA3AF]">· score {reference.score}</span>
          </div>
        </div>
      </div>
      <p className="text-[12.5px] text-[#374151] leading-relaxed line-clamp-4 mb-3 italic">
        “{reference.chunk_text}”
      </p>
      <div className="flex items-center gap-1.5">
        <button
          onClick={handleOpenSource}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded-lg text-[11px] font-semibold text-white bg-[#6590FF] hover:bg-[#4B7CFF] transition-colors"
          data-testid="leaf-ref-open-source"
        >
          <ArrowUpRightIcon /> Open source
        </button>
        <button
          onClick={handleAskAbout}
          className="inline-flex items-center justify-center gap-1.5 h-7 px-2.5 rounded-lg text-[11px] font-medium text-[#4B5563] border border-[#E5E7EB] hover:bg-[#F3F4F6] transition-colors"
          data-testid="leaf-ref-ask-about"
        >
          Ask about this
        </button>
      </div>
    </div>
  );
}

// Lightweight inline icon helpers — avoids growing the lucide import surface area.
function Loader2Icon() {
  return <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" /></svg>;
}
function ArrowUpRightIcon() {
  return <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7M9 7h8v8" /></svg>;
}

// Lazy-load SourceDetailPopup to avoid a circular import (it imports from this file's siblings).
function LazySourceDetailPopup(props) {
  const [Component, setComponent] = useState(null);
  useEffect(() => {
    let cancelled = false;
    import('./SourcesPanel').then(m => {
      if (!cancelled) setComponent(() => m.SourceDetailPopup);
    });
    return () => { cancelled = true; };
  }, []);
  if (!Component) return null;
  return <Component {...props} initialTab="content" />;
}
